import { lazy, startTransition, Suspense, useCallback } from 'react';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { type SearchState, SearchStoreProvider } from '@pkgs/components/hooks/stores/search/SearchStoreProvider';
import { Drawer } from '@base-ui/react/drawer';

const AdvancedFiltersContent = lazy(() => import('./AdvancedFiltersContent'));

import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { initialPropertyTypeState } from '@pkgs/components/hooks/stores/search/slices/objectTypeFilterSlice';

import { AdvancedFiltersButton, type AdvancedFiltersButtonVariant } from './AdvancedFiltersButton';

import styles from './AdvancedFilters.module.css';

type AdvancedFiltersDrawerProps = {
  className?: string;
  variant: AdvancedFiltersButtonVariant;
};
export const AdvancedFiltersDrawer = ({ className, variant }: AdvancedFiltersDrawerProps) => {
  const applyState = useSearchStore((state) => state.applyState);
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const setPromptValue = usePromptStore((state) => state.setPromptValue);

  const handleDone = useCallback(
    (newState: SearchState) => {
      startTransition(() => {
        // reset property type state for non-selected object type categories
        const patch = {
          propertyTypeState: {
            ...initialPropertyTypeState,
            [newState.objectTypeCategory]: newState.propertyTypeState[newState.objectTypeCategory],
          },
        };

        applyState({ state: { ...newState, ...patch } });
        setPromptValue('');
      });
    },
    [applyState, setPromptValue],
  );

  return (
    <Drawer.Root swipeDirection="right">
      <AdvancedFiltersButton variant={variant} className={className} />
      <Drawer.Portal>
        <Drawer.Backdrop className={styles.drawerBackdrop} data-testid="search-components_advanced-filters" />
        <Drawer.Viewport className={styles.drawerViewport}>
          <Drawer.Popup className={styles.drawerPopup}>
            <Drawer.Content className={styles.drawerContent}>
              <Suspense fallback={null}>
                <SearchStoreProvider initialState={() => getStateSnapshot()} namespace="advanced-filters">
                  <AdvancedFiltersContent onDone={handleDone} />
                </SearchStoreProvider>
              </Suspense>
            </Drawer.Content>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
